import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RESEARCH_STATUSES } from '@commerce/shared';
import { api, type ApiListMeta } from '../../lib/api.js';
import type { ResearchItem } from '../../lib/types.js';
import { StatusBadge } from '../../components/StatusBadge.js';

export function ResearchListPage() {
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');

  const query = useQuery({
    queryKey: ['research', { status, q }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (q) params.set('q', q);
      return api.get<ResearchItem[]>(`/research?${params.toString()}`) as Promise<{
        data: ResearchItem[];
        meta: ApiListMeta;
      }>;
    },
  });

  return (
    <section>
      <div className="page-head">
        <h1>Research</h1>
        <Link className="button" to="/research/new">
          New research
        </Link>
      </div>

      <div className="filters">
        <label>
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            {RESEARCH_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </label>
        <label>
          Search
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="title or summary" />
        </label>
      </div>

      {query.isLoading && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form-error">
          Failed to load research.
        </p>
      )}
      {query.data && query.data.data.length === 0 && <p className="muted">No research yet.</p>}

      {query.data && query.data.data.length > 0 && (
        <table className="data-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>Signal</th>
              <th>Confidence</th>
              <th>Source</th>
              <th>Observed</th>
            </tr>
          </thead>
          <tbody>
            {query.data.data.map((item) => (
              <tr key={item.id}>
                <td>
                  <Link to={`/research/${item.id}`}>{item.title}</Link>
                </td>
                <td>
                  <StatusBadge status={item.status} />
                </td>
                <td>{item.signalType}</td>
                <td>{Math.round(item.confidence * 100)}%</td>
                <td>{item.sourceDomain}</td>
                <td>{new Date(item.observedAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
